import { aboutBlocks, contactInfo, principles, statsList, testimonials } from "@/mocks/site";
import { Reveal, SectionHead } from "./ui";

function Stars({ n }: { n: number }) {
  return (
    <span className="flex gap-0.5 text-accent-500">
      {Array.from({ length: n }).map((_, i) => (
        <i key={i} className="ri-star-fill text-xs" aria-hidden="true"></i>
      ))}
    </span>
  );
}

export default function About() {
  return (
    <section id="about" className="scroll-mt-20 bg-background-50 py-16 md:py-24">
      <div className="mx-auto max-w-[1320px] px-4 md:px-6">
        {/* stats band */}
        <Reveal>
          <div className="mb-16 overflow-hidden rounded-2xl bg-foreground-950">
            <div className="grid grid-cols-2 divide-x divide-background-100/10 lg:grid-cols-4">
              {statsList.map((s) => (
                <div key={s.label} className="flex flex-col items-center justify-center gap-1.5 px-4 py-8 text-center">
                  <span className="font-heading text-2xl font-black text-accent-400 md:text-4xl">
                    {s.value}
                  </span>
                  <span className="text-xs text-background-100/80 md:text-sm">{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <SectionHead
          index="07"
          en="About us"
          title="关于吃瓜前线"
          desc="我们是一群坚持“先别急、等核实”的吃瓜复盘者。站得再近一点，看得再远一些——只为让你每次吃到的都是完整版的瓜。"
          align="center"
        />

        {/* about blocks */}
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {aboutBlocks.map((b, i) => (
            <Reveal key={b.title} delay={i * 90}>
              <article className="flex h-full flex-col rounded-2xl border border-background-200 bg-background-100/50 p-6">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-500/10 text-2xl text-primary-600">
                  <i className={b.icon} aria-hidden="true"></i>
                </span>
                <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{b.title}</h3>
                <p className="mt-2.5 text-sm leading-7 text-foreground-600">{b.text}</p>
              </article>
            </Reveal>
          ))}
        </div>

        {/* principles */}
        <div className="mt-16 grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-start">
          <Reveal>
            <h3 className="mb-5 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
              <i className="ri-shield-check-line text-accent-500" aria-hidden="true"></i>
              内容原则
            </h3>
            <ul className="flex flex-col gap-3">
              {principles.map((p, i) => (
                <li key={p} className="flex items-start gap-3 rounded-xl bg-background-100/60 px-4 py-3.5">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-500 font-heading text-xs font-bold text-background-50">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-sm leading-6 text-foreground-700">{p}</span>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={120}>
            <h3 className="mb-5 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
              <i className="ri-double-quotes-l text-primary-500" aria-hidden="true"></i>
              读者口碑
            </h3>
            <div className="flex flex-col gap-4">
              {testimonials.map((t) => (
                <figure key={t.name} className="rounded-2xl border border-background-200 bg-background-100/50 p-5">
                  <Stars n={t.stars} />
                  <blockquote className="mt-3 text-sm leading-7 text-foreground-700">
                    “{t.text}”
                  </blockquote>
                  <figcaption className="mt-3 flex items-center gap-2 text-xs font-medium text-secondary-600">
                    <i className="ri-user-smile-line text-base" aria-hidden="true"></i>
                    {t.name}
                  </figcaption>
                </figure>
              ))}
            </div>
          </Reveal>
        </div>

        {/* contact */}
        <Reveal>
          <div className="mt-16 flex flex-col items-start justify-between gap-6 rounded-2xl bg-accent-100/70 p-6 ring-1 ring-accent-200 md:flex-row md:items-center md:p-8">
            <div>
              <h3 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                {contactInfo.slogan}
              </h3>
              <p className="mt-2 text-sm text-foreground-600">{contactInfo.note}</p>
            </div>
            <a
              href={`mailto:${contactInfo.email}`}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-accent-600"
            >
              <i className="ri-mail-send-line" aria-hidden="true"></i>
              {contactInfo.email}
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}